import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Bell, BellOff, Loader2, Clock, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NEXT = { new: "preparing", preparing: "ready" };
const LABELS = { new: "Start preparing", preparing: "Mark ready" };

const playChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination); o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.05);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    o.start(); o.stop(ctx.currentTime + 0.5);
  } catch { /* no audio */ }
};

const ago = (iso) => {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return mins < 1 ? "just now" : `${mins}m ago`;
};

const Card = ({ o, onStatus }) => (
  <div data-testid={`station-order-${o.id}`} className={`rounded-2xl border-2 bg-white p-3 ${o.status === "new" ? "border-amber-400" : "border-sea/40"}`}>
    <div className="flex items-center justify-between">
      <span className="font-display text-xl text-slate-900">{o.spot_label}</span>
      <span className="flex items-center gap-1 text-[11px] text-slate-400"><Clock size={11} /> {ago(o.created_at)}</span>
    </div>
    {o.allergies && <div data-testid={`station-allergy-${o.id}`} className="mt-2 flex items-center gap-1.5 rounded-lg border-2 border-destructive bg-destructive/15 px-2 py-1.5 text-sm font-bold uppercase text-destructive"><AlertTriangle size={15} /> Allergies: {o.allergies}</div>}
    <ul className="mt-2 space-y-1">
      {o.items.map((it, i) => (
        <li key={`${it.name}-${i}`} className="text-sm">
          <span className="font-semibold text-slate-800">{it.qty}× {it.name}</span>
          {it.options?.length ? <span className="text-slate-500"> · {it.options.join(" · ")}</span> : null}
          {it.note ? <span className="block pl-4 text-xs italic text-slate-500">“{it.note}”</span> : null}
        </li>
      ))}
    </ul>
    {o.note && <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs italic text-amber-800">“{o.note}”</p>}
    {o.source === "waiter" && <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-brand-700">via waiter</p>}
    {NEXT[o.status] && (
      <button data-testid={`station-advance-${o.id}`} onClick={() => onStatus(o.id, NEXT[o.status])}
        className={`mt-2 h-10 w-full rounded-xl text-sm font-semibold text-white ${o.status === "new" ? "bg-sea hover:bg-sea/90" : "bg-sage hover:bg-sage/90"}`}>{LABELS[o.status]}</button>
    )}
  </div>
);

export default function StationBoard({ station, title, Icon }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const soundKey = `orderSoundMuted_${station}`;
  const [muted, setMuted] = useState(() => localStorage.getItem(soundKey) === "1");
  const seen = useRef(null);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const today = format(new Date(), "yyyy-MM-dd");

  useEffect(() => {
    let alive = true;
    seen.current = null;
    const fetchOrders = () => axios.get(`${API}/orders`, { params: { date: today, station } })
      .then((r) => {
        if (!alive) return;
        setOrders(r.data); setLoading(false);
        const ids = new Set(r.data.map((o) => o.id));
        if (seen.current) {
          const fresh = r.data.filter((o) => !seen.current.has(o.id) && o.status === "new");
          if (fresh.length && !mutedRef.current) playChime();
        }
        seen.current = ids;
      }).catch(() => setLoading(false));
    fetchOrders();
    const t = setInterval(fetchOrders, 12000);
    return () => { alive = false; clearInterval(t); };
  }, [today, station]);

  const onStatus = async (id, status) => {
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
    try { await axios.patch(`${API}/orders/${id}/status`, { status }); } catch { toast.error("Could not update"); }
  };
  const toggleMute = () => { const n = !muted; setMuted(n); localStorage.setItem(soundKey, n ? "1" : "0"); };

  const news = orders.filter((o) => o.status === "new");
  const prep = orders.filter((o) => o.status === "preparing");
  const served = orders.filter((o) => o.status === "served" || o.status === "ready");

  return (
    <div className="mx-auto max-w-6xl p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-2xl tracking-wide text-slate-900"><Icon size={22} className="text-brand-700" /> {title}</h2>
        <button data-testid="station-mute-toggle" onClick={toggleMute}
          className={`flex h-10 items-center gap-2 rounded-full border px-4 text-sm font-semibold ${muted ? "border-sand-300 bg-white text-slate-500" : "border-brand bg-brand/10 text-brand-700"}`}>
          {muted ? <BellOff size={16} /> : <Bell size={16} />} {muted ? "Sound off" : "Sound on"}
        </button>
      </div>

      {loading ? <div className="flex justify-center py-20 text-slate-400"><Loader2 className="animate-spin" /></div> : (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-amber-600">New · {news.length}</p>
              <div className="space-y-3" data-testid="station-new">
                {news.length === 0 ? <p className="rounded-xl bg-white/60 py-6 text-center text-sm text-slate-400">No new orders</p> : news.map((o) => <Card key={o.id} o={o} onStatus={onStatus} />)}
              </div>
            </div>
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-sea">Preparing · {prep.length}</p>
              <div className="space-y-3" data-testid="station-preparing">
                {prep.length === 0 ? <p className="rounded-xl bg-white/60 py-6 text-center text-sm text-slate-400">Nothing in prep</p> : prep.map((o) => <Card key={o.id} o={o} onStatus={onStatus} />)}
              </div>
            </div>
          </div>

          {served.length > 0 && (
            <div className="mt-8">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Served today · {served.length}</p>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="station-served">
                {served.map((o) => (
                  <div key={o.id} className="rounded-xl border border-sand-200 bg-white/60 p-2.5 opacity-70">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-700">{o.spot_label}</span>
                      <span className="text-[11px] text-slate-400">{ago(o.served_at || o.created_at)}</span>
                    </div>
                    <p className="text-xs text-slate-500">{o.items.reduce((s, i) => s + i.qty, 0)} items · {o.total.toFixed(2)}€</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
